import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { CircleAlert, CreditCard, Loader2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { SaveStatus } from "@/components/SaveStatus";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import type { ApiError } from "@/lib/api/unwrap";

import { useQueryClient } from "@tanstack/react-query";

import {
  billingKeys,
  useBillingInformation,
  useSetupIntent,
  useUpdatePaymentMethod,
} from "./api";
import { getStripe, isStripeConfigured, stripeAppearance } from "./stripe";

/**
 * Stripe Elements form that collects and saves a card against a SetupIntent.
 *
 * The card fields are rendered by Stripe (PCI-compliant iframe via
 * <PaymentElement>). We request a SetupIntent client_secret from the API, mount
 * <Elements> with it, then confirm the setup client-side. `redirect:
 * "if_required"` keeps card confirmation inline (no full-page redirect).
 */
function CardSetupForm({ onSaved }: { onSaved: () => void }) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const updatePaymentMethod = useUpdatePaymentMethod();
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">(
    "idle",
  );
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) return;

    setStatus("saving");
    setError(null);
    const result = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: "if_required",
    });

    if (result.error) {
      setStatus("error");
      setError(result.error.message ?? t("billing.payment.genericError"));
      return;
    }

    // The new card is attached; make it the default and detach any prior card so
    // only one stays on file (F-b). Best-effort — the card is already saved even
    // if pruning fails, so don't block the "saved" state on it.
    const pm = result.setupIntent?.payment_method;
    const paymentMethodId = typeof pm === "string" ? pm : pm?.id;
    if (paymentMethodId) {
      try {
        await updatePaymentMethod.mutateAsync(paymentMethodId);
      } catch {
        // ignore — the card was saved; pruning the old one can be retried later
      }
    }

    setStatus("saved");
    onSaved();
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      {error && (
        <p className="flex items-center gap-1.5 text-sm font-medium text-destructive">
          <CircleAlert className="size-4" aria-hidden />
          {error}
        </p>
      )}
      <div className="flex items-center gap-3">
        <Button type="submit" disabled={!stripe || status === "saving"}>
          {status === "saving" && (
            <Loader2 className="size-4 animate-spin" aria-hidden />
          )}
          {t("billing.payment.saveCard")}
        </Button>
        {status === "saved" && <SaveStatus status="saved" />}
      </div>
    </form>
  );
}

export function PaymentCard() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const setupIntent = useSetupIntent();
  const clientSecret = setupIntent.data?.client_secret;

  // Enforce order: the Stripe Customer is created from the billing information,
  // so the card fields only appear once that info is complete. Mirrors the old
  // app, where the customer is bootstrapped on billing-info save.
  const billingInfo = useBillingInformation();
  const info = billingInfo.data;
  const billingComplete = Boolean(
    info?.name?.trim() &&
      info?.address?.trim() &&
      info?.city?.trim() &&
      info?.zip_code?.trim(),
  );

  // After a card is saved, the active-subscription summary shows the card on
  // file — refresh it so the display isn't stale.
  const handleSaved = () => {
    setupIntent.reset();
    void queryClient.invalidateQueries({ queryKey: billingKeys.status() });
  };

  const body = () => {
    if (!isStripeConfigured) {
      return (
        <p className="text-muted-foreground">{t("billing.payment.notConfigured")}</p>
      );
    }

    if (!billingComplete) {
      return (
        <p className="text-muted-foreground">
          {t("billing.payment.completeInfoFirst")}
        </p>
      );
    }

    if (clientSecret) {
      return (
        <Elements
          stripe={getStripe()}
          options={{ clientSecret, appearance: stripeAppearance }}
        >
          <CardSetupForm onSaved={handleSaved} />
        </Elements>
      );
    }

    const err = setupIntent.error as unknown as ApiError | null;
    return (
      <div className="space-y-3">
        {err && (
          <p className="flex items-center gap-1.5 text-sm font-medium text-destructive">
            <CircleAlert className="size-4" aria-hidden />
            {err.detail ?? t("billing.payment.setupError")}
          </p>
        )}
        <Button
          type="button"
          variant="outline"
          onClick={() => setupIntent.mutate()}
          disabled={setupIntent.isPending}
        >
          {setupIntent.isPending ? (
            <Loader2 className="size-4 animate-spin" aria-hidden />
          ) : (
            <CreditCard className="size-4" aria-hidden />
          )}
          {t("billing.payment.addCard")}
        </Button>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("billing.payment.title")}</CardTitle>
        <CardDescription>{t("billing.payment.description")}</CardDescription>
      </CardHeader>
      <CardContent>{body()}</CardContent>
    </Card>
  );
}
